import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import axios from "axios";
import UploadIcon from "@mui/icons-material/Upload";
import SvgIcon from "../ipd-billing/SvgIcon";
import { C, getAuthHeaders } from "../ipd-billing/constants";
import { BrandingContext } from "../../BrandingProvider";
import { usePermissions } from "../login/PermissionContext";
import {
  postCharge,
  bulkPostCharges,
  cancelCharge,
  getChargeEvents,
} from "../ipd-billing/billing-api";
import { cardStyle, inputStyle } from "../rule-builder/ui";
import UploadFileModal from "../appointments/UploadFileModal";
import DispatchToVendorModal from "../lab-technician/DispatchToVendorModal";
import {
  readDispatchMap,
  saveDispatch,
} from "../lab-technician/api/outsourcing";
import {
  captureVendorChargesForOrders,
  resolveBillingScope,
  reverseVendorCharges,
  canShowVendorFlow,
  validMedicalServiceId,
} from "../lab-technician/api/vendorChargeCapture";
import {
  READY_STATUSES,
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  MODALITY_OPTIONS,
  formatDateTime,
  STATUS_META,
  PriorityPill,
  groupOrdersByBatchOnly,
  groupOrdersByModality,
  withSalutation,
  fetchRadiologyTestsPage,
  fetchAllRadiologyTestPatients,
  computeRadiologyDashboardStats,
  latestActivityMs,
  lookupRadiologyPatientLabTestId,
  pickRadiologyPatientLabTestId,
  isDiagnosticReportRejected,
  isDiagnosticReportPublished,
} from "./radiologyOrdersShared";
import RadiologyReportModal from "./RadiologyReportModal";
import { fetchReportsMap } from "./api/radiologyReportApi";
import { downloadRadiologyReportPdf } from "./utils/printRadiologyReport";
import AssignDiagnosticDoctorsModal from "../diagnostic-reports/AssignDiagnosticDoctorsModal";
import { sendRadiologyOrderToRis } from "../billing-payment/utils/risIntegration";
import {
  RADIOLOGY_SERVICE_GROUP_ID,
  fetchDiagnosticReview,
  isReviewSubmitted,
  markReviewSubmitted,
  markReviewRevised,
} from "../diagnostic-reports/diagnosticReportApi";

// ─── Live data: GET get_ipd_radiology_tests/?page=&page_size=&… ───────────────
// Paginated All/OPD/IPD board. Filters are server-side query params (modality
// stays client-side). Detail on expand: same endpoint with admission_id or
// radiology_test_id / lab_test_id.
const DEFAULT_PAGE_SIZE = 25;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const latestOrderMs = (orders) => latestActivityMs(orders);

// Billing charge-capture wants the numeric service master number (CPT column),
// never the UUID medicalServiceId used by report templates.
const billingMedicalServiceId = (order) => {
  const raw = order?.medicalServiceNo;
  if (raw == null || raw === "" || raw === "-") return null;
  const s = String(raw).trim();
  if (UUID_RE.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) && String(n) === s ? n : s;
};

const todayChargeDate = () => new Date().toISOString().split("T")[0];

const unwrapBillingPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return payload.data;
  }
  return payload;
};

const extractChargeEventId = (payload) => {
  const root = unwrapBillingPayload(payload) || {};
  return (
    root.charge_event?.id ||
    root.charge_event_id ||
    root.id ||
    null
  );
};

// Map bulk-post results back onto the orders we just billed. Shape varies —
// try common envelopes, then match by medical_service_id when present.
const extractBulkChargeResults = (payload) => {
  const root = unwrapBillingPayload(payload) || {};
  const list = Array.isArray(root)
    ? root
    : root.charges ||
      root.results ||
      root.items ||
      root.created ||
      root.posted ||
      [];
  if (!Array.isArray(list)) return [];
  return list.map((item) => {
    const ce = item?.charge_event && typeof item.charge_event === "object"
      ? item.charge_event
      : item;
    return {
      chargeEventId:
        item?.charge_event?.id ||
        item?.charge_event_id ||
        ce?.id ||
        item?.id ||
        null,
      medicalServiceId:
        item?.medical_service_id ??
        ce?.medical_service_id ??
        null,
    };
  });
};

const matchChargeEventIdForOrder = (order, results) => {
  const svcId = billingMedicalServiceId(order);
  if (svcId == null) return null;
  const hit = (results || []).find(
    (r) =>
      r.chargeEventId &&
      r.medicalServiceId != null &&
      String(r.medicalServiceId) === String(svcId),
  );
  return hit?.chargeEventId || null;
};

// After a refresh, recover charge_event_id from GET …/billing/charges/ so
// Cancel still works when the in-memory map was lost.
const resolveChargeEventIdFromLedger = async (admissionId, order) => {
  if (order?.chargeEventId) return order.chargeEventId;
  const svcId = billingMedicalServiceId(order);
  if (!admissionId || svcId == null) return null;
  const events = await getChargeEvents(admissionId);
  const cancelledParents = new Set(
    (events || [])
      .filter((e) => String(e?.event_type || "").toUpperCase() === "CANCELLATION")
      .map((e) => e?.parent_event_id || e?.metadata?.original_charge_event_id)
      .filter(Boolean),
  );
  const open = (events || []).filter((e) => {
    const type = String(e?.event_type || "CHARGE").toUpperCase();
    if (type !== "CHARGE") return false;
    const id = e?.id || e?.charge_event_id;
    return id && !cancelledParents.has(id);
  });
  const matches = open.filter(
    (e) => String(e?.medical_service_id ?? "") === String(svcId),
  );
  if (matches.length === 0) return null;
  if (matches.length === 1) {
    return matches[0].id || matches[0].charge_event_id || null;
  }
  const sorted = [...matches].sort((a, b) => {
    const ta = Date.parse(a.event_time || a.created_at || 0) || 0;
    const tb = Date.parse(b.event_time || b.created_at || 0) || 0;
    return tb - ta;
  });
  return sorted[0].id || sorted[0].charge_event_id || null;
};

const CANCEL_REASON_PRESETS = [
  "Entered in error",
  "Duplicate order",
  "Service not rendered",
  "Patient refused",
  "Ordered in error",
];

const isIpdPatient = (patient) =>
  patient?.patientType === "IPD" ||
  (patient?.admissionId != null && patient.admissionId !== "");

const SEND_TO_OUTSOURCED = "Send to Outsourced";
// ris_integrated: the study goes to the RIS instead of Started → Completed here.
const SENT_TO_LAB = "Sent to lab";

const resolveOpdLabTestId = (patient, order) =>
  String(
    order?.radiologyTestId ||
      order?.labTestId ||
      patient?.radiologyTestId ||
      patient?.labTestId ||
      order?.orderNo ||
      "",
  ).trim();

const samePatientRow = (a, b) => {
  if (!a || !b) return false;
  if (a.id && b.id && String(a.id) === String(b.id)) return true;
  // patientRows stamps `${p.id}::${batchKey}` even for a single batch — OPD
  // Acknowledge/status writes must still hit the un-suffixed patients entry.
  if (a.id && b.id) {
    const aId = String(a.id);
    const bId = String(b.id);
    if (aId.startsWith(`${bId}::`) || bId.startsWith(`${aId}::`)) return true;
  }
  if (
    isIpdPatient(a) &&
    isIpdPatient(b) &&
    a.admissionId != null &&
    String(a.admissionId) === String(b.admissionId)
  ) {
    return true;
  }
  if (
    a.patientType === "EMERGENCY" &&
    b.patientType === "EMERGENCY" &&
    a.visitId != null &&
    b.visitId != null &&
    String(a.visitId) === String(b.visitId)
  ) {
    return true;
  }
  const aAppt = a.appointmentId;
  const bAppt = b.appointmentId;
  if (aAppt && bAppt && String(aAppt) === String(bAppt)) {
    if (!a.patientId || !b.patientId) return true;
    return String(a.patientId) === String(b.patientId);
  }
  return false;
};

const assertStatusUpdateOk = (res) => {
  if (res?.data?.is_valid === false || res?.data?.status === "Failed") {
    throw new Error(res.data?.message || "Request rejected by server");
  }
  return res;
};

/** Replace page results while preserving optimistic status advances. */
const replacePatientsPreservingStatus = (prev, next, serverAuthoritative) => {
  const prevById = new Map(prev.map((p) => [p.id, p]));
  return next.map((np) => {
    const pp = prevById.get(np.id);
    if (!pp) return np;
    return {
      ...np,
      orders: reconcileOrderStatuses(
        pp.orders,
        np.orders,
        serverAuthoritative,
      ),
    };
  });
};

const statusRank = (status) => {
  const idx = STATUS_OPTIONS.indexOf(status);
  return idx === -1 ? 0 : idx;
};

const furthestStatus = (...statuses) =>
  statuses
    .filter(Boolean)
    .reduce(
      (best, s) => (statusRank(s) > statusRank(best) ? s : best),
      statuses.find(Boolean) || "Ordered",
    );

// A refetch must never walk a status backwards on its own (the bulk list can lag
// behind an acknowledgement or a Started/In Progress write). Keep the furthest
// local/persisted value until the server catches up.
const reconcileOrderStatuses = (prevOrders, nextOrders, serverAuthoritative) => {
  if (!prevOrders?.length) return nextOrders;
  const prevByNo = new Map(prevOrders.map((o) => [o.orderNo, o]));
  return nextOrders.map((no) => {
    const po = prevByNo.get(no.orderNo);
    if (!po) return no;
    // A slot booked in this session outlives a refetch that comes back without
    // one: a payload with no scheduled_time means "nothing to say", not
    // "cleared" — otherwise the time the technician just picked blanks out on
    // the next refresh.
    let merged =
      !no.scheduled && po.scheduled ? { ...no, scheduled: po.scheduled } : no;
    // Keep session charge_event_id across refetches so Cancel can reverse the
    // ledger without another lookup when the bulk list has no billing fields.
    if (!merged.chargeEventId && po.chargeEventId) {
      merged = { ...merged, chargeEventId: po.chargeEventId };
    }
    const persistedHint = serverAuthoritative?.has(merged.orderNo)
      ? po.status
      : null;
    const keep = furthestStatus(merged.status, po.status, persistedHint);
    const next = keep === merged.status ? merged : { ...merged, status: keep };
    return {
      ...next,
      reportPublishStatus:
        merged.reportPublishStatus || po.reportPublishStatus || null,
      rejectionReason: merged.rejectionReason || po.rejectionReason || "",
    };
  });
};

const KPI_META = [
  { key: "total", label: "Total Orders", icon: "invoice", bg: "#E9F5FE", color: C.primary },
  { key: "ordered", label: "Ordered", icon: "clock", bg: "#F3F4F6", color: "#374151" },
  { key: "scheduled", label: "Scheduled", icon: "calendar", bg: "#ECFEFF", color: "#0E7490" },
  { key: "acknowledged", label: "Acknowledged", icon: "check", bg: "#EEF2FF", color: "#4338CA" },
  { key: "started", label: "Started", icon: "sync", bg: "#FEFCE8", color: "#A16207" },
  { key: "inProgress", label: "In Progress", icon: "sync", bg: "#FFF7ED", color: "#C2410C" },
  { key: "completed", label: "Completed", icon: "check", bg: "#ECFDF5", color: "#15803D" },
  { key: "reported", label: "Reported", icon: "reports", bg: "#F0FDFA", color: "#0F766E" },
  { key: "verified", label: "Verified", icon: "shield", bg: "#F0FDFA", color: "#0F766E" },
  { key: "cancelled", label: "Cancelled", icon: "x", bg: "#FEF2F2", color: "#B91C1C" },
  { key: "today", label: "Today", icon: "clock", bg: "#FEF2F2", color: "#B91C1C" },
];

const isInDateRange = (iso, fromStr, toStr) => {
  if (!iso) return true;
  const date = iso.slice(0, 10);
  if (fromStr && date < fromStr) return false;
  if (toStr && date > toStr) return false;
  return true;
};

const EMPTY_KPI_STATS = {
  total: 0,
  ordered: 0,
  scheduled: 0,
  acknowledged: 0,
  started: 0,
  inProgress: 0,
  completed: 0,
  reported: 0,
  verified: 0,
  cancelled: 0,
  today: 0,
};

function KpiStrip({ stats, loading }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: 12,
        marginBottom: 20,
      }}
    >
      {KPI_META.map((k) => (
        <div
          key={k.key}
          style={{
            ...cardStyle,
            padding: "12px 14px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              background: k.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <SvgIcon name={k.icon} size={16} color={k.color} />
          </div>
          <div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: C.textSub,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {k.label}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>
              {loading ? "…" : stats[k.key]}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const formatPatientLocation = (patient) => {
  const room =
    patient?.roomNo && patient.roomNo !== "-"
      ? String(patient.roomNo).trim()
      : "";
  const bed =
    patient?.bedNo && patient.bedNo !== "-" ? String(patient.bedNo).trim() : "";
  const age =
    patient?.age !== null && patient?.age !== undefined && patient.age !== ""
      ? `${patient.age} Y`
      : "";
  let location = "-";
  if (room && bed) location = `${room} · Bed ${bed}`;
  else if (room) location = room;
  else if (bed) location = `Bed ${bed}`;
  // OPD (no bed/room): show OPD instead of blank/-.
  if (
    location === "-" &&
    (patient?.patientType === "OPD" || patient?.patientType === "")
  ) {
    location = "OPD";
  }
  // An emergency order has no bed or room either, but it is not a walk-in —
  // say where it came from rather than leaving a bare dash.
  if (location === "-" && patient?.patientType === "EMERGENCY") {
    location = "Emergency";
  }
  if (age) return location === "-" ? age : `${age} · ${location}`;
  return location;
};

const formatOrderNoLabel = (orderNo) => {
  const s = String(orderNo || "").trim();
  if (s.length <= 18) return s;
  if (/^[0-9a-f-]{20,}$/i.test(s)) return `${s.slice(0, 8)}…${s.slice(-4)}`;
  return `${s.slice(0, 14)}…`;
};

// ── Acknowledge gate ────────────────────────────────────────────────────────
// Before radiology takes the study on, the status cell only offers
// Acknowledged or Cancelled (plus the current Ordered/Scheduled value so the
// native <select> can display it). Booking a slot marks the study Scheduled —
// that is still awaiting acknowledgement. Once acknowledged, neither
// Acknowledged nor Cancelled is a choice: the run is Started → In Progress →
// Completed, and "Reported" is reached only by finalizing a report.
const ACK_PENDING_STATUSES = ["Ordered", "Scheduled"];

const ordersAwaitingAck = (orders) =>
  (orders || []).filter((o) => ACK_PENDING_STATUSES.includes(o.status));

const allOrdersAre = (orders, status) =>
  (orders || []).length > 0 && orders.every((o) => o.status === status);

const summarizeRowStatus = (orders) => {
  if (!orders?.length) return "Ordered";
  if ((orders || []).every((o) => ["Reported", "Verified"].includes(o.status))) {
    return (orders || []).some((o) => o.status === "Verified")
      ? "Verified"
      : "Reported";
  }
  if (allOrdersAre(orders, "Cancelled")) return "Cancelled";
  const pending = ordersAwaitingAck(orders);
  if (pending.length > 0) {
    return pending.some((o) => o.status === "Ordered") ? "Ordered" : "Scheduled";
  }
  const started = (orders || []).find((o) =>
    ["Started", "In Progress", "Completed", SENT_TO_LAB].includes(o.status),
  );
  return started?.status || "Acknowledged";
};

function RowStatusLabel({ status, acknowledgedAt, submitted = false }) {
  const meta = STATUS_META[status] || { bg: "#F3F4F6", color: "#374151" };
  if (submitted && status === "Reported") status = "Submitted for Review";
  const showCheck =
    status === "Acknowledged" ||
    status === "Reported" ||
    status === "Verified" ||
    status === "Completed";
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          fontSize: 12,
          fontWeight: 700,
          color: meta.color,
        }}
      >
        {showCheck && <SvgIcon name="check" size={13} color={meta.color} />}
        {status}
      </div>
      {acknowledgedAt && status === "Acknowledged" && (
        <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>
          {formatDateTime(acknowledgedAt)}
        </div>
      )}
    </div>
  );
}

const ACK_TIME_OPTIONS = ["Acknowledged", "Cancelled"];
const POST_ACK_STATUS_OPTIONS = ["Started", "In Progress", "Completed"];

const statusDropdownOptions = (
  status,
  { canOutsource = false, alreadyOutsourced = false, risOn = false } = {},
) => {
  if (ACK_PENDING_STATUSES.includes(status)) {
    return [status, ...ACK_TIME_OPTIONS];
  }
  if (alreadyOutsourced) return null;
  if (status === "Acknowledged") {
    const opts = [
      "Acknowledged",
      ...(risOn ? [SENT_TO_LAB] : POST_ACK_STATUS_OPTIONS),
    ];
    if (canOutsource) opts.push(SEND_TO_OUTSOURCED);
    return opts;
  }
  // Once work has started in-house the study can no longer be outsourced.
  if (POST_ACK_STATUS_OPTIONS.includes(status)) {
    return [...POST_ACK_STATUS_OPTIONS];
  }
  return null;
};

const statusTextStyle = (meta) => ({
  padding: 0,
  border: "none",
  background: "transparent",
  color: meta.color,
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "inherit",
  outline: "none",
});

function StatusSelect({
  order,
  disabled,
  onChange,
  canOutsource = false,
  dispatch = null,
  risOn = false,
}) {
  const alreadyOutsourced = !!dispatch;
  const meta = STATUS_META[order.status] || { bg: "#F3F4F6", color: "#374151" };
  if (order.status === "Reported" && isReviewSubmitted(order)) {
    return <span style={statusTextStyle(meta)}>Submitted for Review</span>;
  }
  if (alreadyOutsourced) {
    return (
      <span style={statusTextStyle(meta)} title={dispatch.vendorName || ""}>
        {`Sent to ${dispatch.vendorName || "vendor"}`}
      </span>
    );
  }
  const options = statusDropdownOptions(order.status, {
    canOutsource,
    alreadyOutsourced,
    risOn,
  });
  if (!options) {
    return <span style={statusTextStyle(meta)}>{order.status}</span>;
  }
  return (
    <select
      value={order.status}
      disabled={disabled}
      onChange={(e) => onChange(order, e.target.value)}
      onClick={(e) => e.stopPropagation()}
      style={{
        ...statusTextStyle(meta),
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {options.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );
}

// Reason is required by …/billing/charges/{id}/cancel/. Confirm before flipping
// the study to Cancelled so an accidental dropdown pick does not reverse a charge.
function CancelOrderModal({ order, patientName, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const canSubmit = reason.trim().length > 0 && !submitting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await onConfirm(reason.trim());
    } catch (err) {
      setError(err?.message || "Failed to cancel order");
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(13,35,63,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: 28,
          width: 440,
          maxWidth: "92vw",
          boxShadow: "0 20px 60px rgba(13,35,63,0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 17,
              fontWeight: 800,
              color: C.text,
            }}
          >
            Cancel study
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 4,
            }}
          >
            <SvgIcon name="x" size={18} color={C.textSub} />
          </button>
        </div>

        <div
          style={{
            background: "#fafbfc",
            border: `1px solid ${C.border}`,
            borderRadius: 9,
            padding: 12,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: C.text,
              marginBottom: 3,
            }}
          >
            {order.service}
          </div>
          <div style={{ fontSize: 12, color: C.textSub }}>
            {patientName || "—"} · {order.orderNo} · CPT {order.cpt}
          </div>
        </div>

        <div>
          <label
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.textSub,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              display: "block",
              marginBottom: 5,
            }}
          >
            Reason for cancellation *
          </label>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              marginBottom: 10,
            }}
          >
            {CANCEL_REASON_PRESETS.map((p) => {
              const active = reason === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setReason(p)}
                  style={{
                    padding: "5px 11px",
                    borderRadius: 100,
                    border: `1.5px solid ${active ? C.primary : C.border}`,
                    background: active ? C.primary : "#fff",
                    color: active ? "#fff" : C.textSub,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {p}
                </button>
              );
            })}
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter or refine the reason…"
            rows={3}
            style={{
              ...inputStyle,
              width: "100%",
              resize: "vertical",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
        </div>

        {error && (
          <div
            style={{
              marginTop: 14,
              background: "#fdecea",
              border: "1px solid #f5c2c7",
              borderRadius: 8,
              padding: "10px 14px",
              fontSize: 12,
              color: "#842029",
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              flex: 1,
              padding: "10px 14px",
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: "#fff",
              color: C.text,
              fontWeight: 600,
              fontSize: 13,
              cursor: submitting ? "default" : "pointer",
              opacity: submitting ? 0.6 : 1,
            }}
          >
            Keep study
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canSubmit}
            style={{
              flex: 2,
              padding: "10px 14px",
              borderRadius: 8,
              border: "none",
              background: canSubmit ? "#B91C1C" : "#FCA5A5",
              color: "#fff",
              fontWeight: 700,
              fontSize: 13,
              cursor: canSubmit ? "pointer" : "default",
            }}
          >
            {submitting ? "Cancelling…" : "Cancel study & reverse charge"}
          </button>
        </div>
      </div>
    </div>
  );
}

// "2026-08-05T10:30" — what <input type="datetime-local"> reads and writes,
// from the ISO-ish string mapApiOrder produces for `scheduled`.
const toDateTimeLocalValue = (iso) => {
  if (!iso) return "";
  const direct = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(String(iso));
  if (direct) return `${direct[1]}T${direct[2]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

// The Scheduled cell is both the value and the control: the booked slot reads
// as text and the calendar icon beside it opens the native picker — the same
// affordance as the date filters above this table. One control per study, never
// per modality group: booking one study must not book the others.
// Scheduling and rescheduling are only allowed before acknowledgement.
function ScheduleCell({ order, disabled, onSchedule }) {
  const inputRef = useRef(null);
  const locked = !ACK_PENDING_STATUSES.includes(order.status);

  const openPicker = () => {
    const el = inputRef.current;
    if (!el) return;
    // showPicker() is the only reliable way to open the calendar for an input
    // this small; focus is the fallback where it isn't supported.
    try {
      if (el.showPicker) el.showPicker();
      else el.focus();
    } catch {
      el.focus();
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          fontSize: 11,
          color: order.scheduled ? C.text : C.textSub,
          whiteSpace: "nowrap",
        }}
      >
        {order.scheduled ? formatDateTime(order.scheduled) : "-"}
      </span>
      {!locked && (
        <span style={{ position: "relative", display: "inline-flex" }}>
          <button
            type="button"
            onClick={openPicker}
            disabled={disabled}
            title={order.scheduled ? "Reschedule this study" : "Schedule this study"}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 4,
              borderRadius: 6,
              border: `1.5px solid ${C.border}`,
              background: "#fff",
              cursor: disabled ? "default" : "pointer",
              opacity: disabled ? 0.6 : 1,
              lineHeight: 0,
            }}
          >
            <SvgIcon name="calendar" size={13} color={C.primary} />
          </button>
          {/* Rendered (so showPicker() is allowed) but invisible — the icon
              above is the affordance, the input is only the picker. */}
          <input
            ref={inputRef}
            type="datetime-local"
            value={toDateTimeLocalValue(order.scheduled)}
            disabled={disabled}
            onChange={(e) => onSchedule(order, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              left: 0,
              bottom: 0,
              width: 1,
              height: 1,
              padding: 0,
              border: "none",
              opacity: 0,
              pointerEvents: "none",
            }}
          />
        </span>
      )}
    </div>
  );
}

function RejectedReportDialog({
  order,
  reason,
  loading,
  onClose,
  onRegenerate,
}) {
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.45)",
          zIndex: 1400,
        }}
      />
      <div
        role="dialog"
        aria-labelledby="rejected-radiology-report-title"
        style={{
          position: "fixed",
          zIndex: 1401,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 420,
          maxWidth: "92%",
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
          padding: "20px 22px 16px",
        }}
      >
        <div
          id="rejected-radiology-report-title"
          style={{ fontSize: 16, fontWeight: 700, color: C.text }}
        >
          Report rejected
        </div>
        <div style={{ fontSize: 12, color: C.textSub, marginTop: 4 }}>
          {order?.orderNo ? `Order ${order.orderNo}` : "This report"} was rejected
          by the reviewing doctor. Status stays Reported until you regenerate
          and submit it again.
        </div>
        <div
          style={{
            marginTop: 14,
            padding: "12px 14px",
            borderRadius: 8,
            border: "1px solid #f5b7b1",
            background: "#fdedec",
            color: "#922b21",
            fontSize: 13,
            lineHeight: 1.45,
            minHeight: 48,
          }}
        >
          {loading
            ? "Loading rejection reason…"
            : reason || "No rejection message was provided."}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 16,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: `1.5px solid ${C.border}`,
              background: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={onRegenerate}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              background: C.primary,
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Regenerate Report
          </button>
        </div>
      </div>
    </>
  );
}

function PatientRowBase({
  patient,
  expanded,
  detailLoading,
  onToggle,
  onDownloadConsolidated,
  onGenerateReport,
  onOpenRejected,
  onUploadReport,
  onStatusChange,
  onSchedule,
  onAcknowledge,
  onSendToOutsourced,
  acknowledgedAt,
  statusUpdatingOrderNos,
  reports,
  dispatchMap,
  risOn,
}) {
  const studiesCount = patient.orders.length;
  const reportableOrders = patient.orders.filter((o) => o.status !== "Cancelled");
  const readyCount = patient.orders.filter(
    (o) =>
      READY_STATUSES.includes(o.status) || reports?.[o.orderNo]?.status === "final",
  ).length;
  const pendingCount = reportableOrders.filter(
    (o) =>
      !READY_STATUSES.includes(o.status) &&
      reports?.[o.orderNo]?.status !== "final",
  ).length;
  const reportableReadyCount = reportableOrders.filter(
    (o) =>
      READY_STATUSES.includes(o.status) || reports?.[o.orderNo]?.status === "final",
  ).length;
  const reportableStudiesCount = reportableOrders.length;
  const latestOrder = patient.orders.reduce(
    (latest, o) => (!latest || o.ordered > latest ? o.ordered : latest),
    null,
  );
  const allReady =
    reportableStudiesCount > 0 &&
    reportableReadyCount === reportableStudiesCount;
  const isPartial =
    reportableStudiesCount > 1 &&
    reportableReadyCount > 0 &&
    reportableReadyCount < reportableStudiesCount;
  const modalityGroups = groupOrdersByModality(patient.orders);
  const rowCanOutsource = canShowVendorFlow(patient.orders, patient);

  return (
    <>
      <tr
        style={{ borderTop: `1px solid ${C.border}`, cursor: "pointer" }}
        onClick={() => onToggle(patient)}
      >
        <td style={{ padding: "10px 12px", width: 28 }}>
          <span
            style={{
              display: "inline-flex",
              transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.15s",
            }}
          >
            <SvgIcon name="chevron-down" size={16} color={C.textSub} />
          </span>
        </td>
        <td style={{ padding: "10px 12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: C.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <SvgIcon name="users" size={14} color={C.primary} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                {patient.name}
              </div>
              <div style={{ fontSize: 11, color: C.textSub }}>
                {formatPatientLocation(patient)}
              </div>
            </div>
          </div>
        </td>
        <td style={{ padding: "10px 12px", fontSize: 13, color: C.text }}>
          {patient.uhid}
        </td>
        <td style={{ padding: "10px 12px", fontSize: 13, color: C.text }}>
          {studiesCount}
        </td>
        <td
          style={{
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 600,
            color: C.success,
          }}
        >
          {readyCount}
        </td>
        <td style={{ padding: "10px 12px", fontSize: 13, color: C.text }}>
          {pendingCount}
        </td>
        <td
          style={{
            padding: "10px 12px",
            fontSize: 12,
            color: C.textSub,
            whiteSpace: "nowrap",
          }}
        >
          {formatDateTime(latestOrder)}
        </td>
        {/* Read-only row status. Acknowledge happens from the per-study
            dropdown inside the expanded panel, not from this cell. */}
        <td style={{ padding: "10px 12px" }}>
          <RowStatusLabel
            status={summarizeRowStatus(patient.orders)}
            submitted={(patient.orders || []).some((o) => isReviewSubmitted(o))}
            acknowledgedAt={acknowledgedAt}
          />
        </td>
        <td
          style={{ padding: "10px 12px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() =>
              onDownloadConsolidated(patient, {
                readyCount: reportableReadyCount,
                studiesCount: reportableStudiesCount,
                allReady,
              })
            }
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 7,
              border: `1.5px solid ${C.border}`,
              background: "#fff",
              color: isPartial ? "#B45309" : C.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <SvgIcon
              name="download"
              size={14}
              color={isPartial ? "#B45309" : C.textSub}
            />
            {isPartial
              ? `Partial (${reportableReadyCount}/${reportableStudiesCount})`
              : "PDF"}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td
            colSpan={9}
            style={{ padding: "12px 16px", background: "#eef6f6", maxWidth: 0 }}
          >
            <div
              style={{
                background: "#fff",
                border: "1.5px solid #21cdc0",
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 2px 10px rgba(33,205,192,0.15)",
              }}
            >
              <div style={{ overflowX: "auto", maxWidth: "100%" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                <thead>
                  <tr
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#fff",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      background: "#0E588E",
                    }}
                  >
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Order No</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Investigation</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Modality</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>CPT</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Priority</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Ordered By</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Order Date</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Scheduled</th>
                    <th style={{ textAlign: "left", padding: "8px 12px" }}>Status</th>
                    <th style={{ textAlign: "left", padding: "8px 12px", minWidth: 100 }}>Report</th>
                  </tr>
                </thead>
                <tbody>
                  {detailLoading && patient.orders.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        style={{
                          padding: "10px 12px",
                          fontSize: 12,
                          color: C.textSub,
                          textAlign: "center",
                        }}
                      >
                        Refreshing studies…
                      </td>
                    </tr>
                  )}
                  {!detailLoading && patient.orders.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        style={{
                          padding: "10px 12px",
                          fontSize: 12,
                          color: C.textSub,
                          textAlign: "center",
                        }}
                      >
                        No studies found for this admission.
                      </td>
                    </tr>
                  )}
                  {modalityGroups.map((group) => {
                    const isMulti = group.orders.length > 1;
                    const primary = group.orders[0];
                    return (
                      <tr
                        key={group.modality}
                        style={{ borderTop: `1px solid ${C.border}` }}
                      >
                        <td
                          style={{
                            padding: "9px 12px",
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.text,
                            maxWidth: 120,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={primary?.orderNo}
                        >
                          {formatOrderNoLabel(primary?.orderNo)}
                        </td>
                        <td style={{ padding: "9px 12px", fontSize: 12, color: C.text }}>
                          {group.orders.map((o, idx) => (
                            <div key={o.orderNo} style={{ marginTop: idx > 0 ? 4 : 0, lineHeight: 1.35 }}>
                              {o.service}
                            </div>
                          ))}
                        </td>
                        {/* Written once for the whole group — that is the point of the grouping. */}
                        <td style={{ padding: "9px 12px", fontSize: 12, color: C.text }}>
                          <div style={{ fontWeight: 700 }}>{group.modality}</div>
                          {isMulti && (
                            <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>
                              {group.orders.length} Studies
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "9px 12px", fontSize: 12, color: C.textSub }}>
                          {group.orders.map((o, idx) => (
                            <div key={o.orderNo} style={{ marginTop: idx > 0 ? 4 : 0, lineHeight: 1.35 }}>
                              {o.cpt && o.cpt !== "-" ? o.cpt : "-"}
                            </div>
                          ))}
                        </td>
                        <td style={{ padding: "9px 12px" }}>
                          {group.orders.map((o) => (
                            <div key={o.orderNo} style={{ marginBottom: isMulti ? 4 : 0 }}>
                              <PriorityPill priority={o.priority} />
                            </div>
                          ))}
                        </td>
                        <td style={{ padding: "9px 12px", fontSize: 12, color: C.text, whiteSpace: "nowrap" }}>
                          <div>
                            {withSalutation(
                              primary?.doctor && primary.doctor !== "-"
                                ? primary.doctor
                                : patient.doctorName,
                            )}
                          </div>
                          {primary?.department && (
                            <div style={{ fontSize: 11, color: C.textSub }}>{primary.department}</div>
                          )}
                        </td>
                        {/* Full timestamp, same as the row's "Latest order" column —
                            two studies placed on one day are otherwise indistinguishable. */}
                        <td style={{ padding: "9px 12px", fontSize: 11, color: C.textSub, whiteSpace: "nowrap" }}>
                          {formatDateTime(primary?.ordered)}
                        </td>
                        {/* Bookable — the calendar icon opens the picker for
                            that one study (see ScheduleCell). */}
                        <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                          {group.orders.map((o) => (
                            <div key={o.orderNo} style={{ marginBottom: isMulti ? 4 : 0 }}>
                              <ScheduleCell
                                order={o}
                                disabled={statusUpdatingOrderNos?.has(o.orderNo)}
                                onSchedule={(target, value) =>
                                  onSchedule(target, value, patient)
                                }
                              />
                            </div>
                          ))}
                        </td>
                        {/* Editable — POST update_lab_test_status/, the same endpoint
                            the lab dashboard and Order Management use. Always one
                            control per study even when the group agrees, since
                            changing one study must not move the others. */}
                        <td style={{ padding: "9px 12px" }}>
                          {group.orders.map((o) => (
                            <div key={o.orderNo} style={{ marginBottom: isMulti ? 4 : 0 }}>
                              <StatusSelect
                                order={o}
                                disabled={statusUpdatingOrderNos?.has(o.orderNo)}
                                canOutsource={
                                  rowCanOutsource &&
                                  validMedicalServiceId(o) != null
                                }
                                dispatch={
                                  dispatchMap?.[String(o.orderNo)] || null
                                }
                                risOn={risOn}
                                onChange={(target, next) => {
                                  if (next === target.status) return;
                                  if (next === "Acknowledged") {
                                    onAcknowledge(patient, [target]);
                                    return;
                                  }
                                  if (next === SEND_TO_OUTSOURCED) {
                                    onSendToOutsourced?.(patient, [target]);
                                    return;
                                  }
                                  onStatusChange(target, next, patient);
                                }}
                              />
                            </div>
                          ))}
                        </td>
                        <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                          {group.orders.map((o) => {
                            const btnStyle = {
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                              padding: "5px 10px",
                              borderRadius: 6,
                              border: `1.5px solid ${C.border}`,
                              background: "#fff",
                              color: C.primary,
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "pointer",
                              fontFamily: "inherit",
                              whiteSpace: "nowrap",
                            };
                            const wrapStyle = {
                              marginBottom: isMulti ? 4 : 0,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flexWrap: "wrap",
                            };
                            if (isDiagnosticReportRejected(o)) {
                              return (
                                <div key={o.orderNo} style={wrapStyle}>
                                  <button
                                    type="button"
                                    title="View rejection and regenerate report"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onOpenRejected?.(o, patient);
                                    }}
                                    style={{
                                      ...btnStyle,
                                      border: "1.5px solid #c0392b",
                                      background: "#fdedec",
                                      color: "#c0392b",
                                      fontWeight: 700,
                                    }}
                                  >
                                    Rejected
                                  </button>
                                  {patient.patientType !== "IPD" ? (
                                    <button
                                      type="button"
                                      title="Upload report to health records"
                                      onClick={() => onUploadReport(patient)}
                                      style={btnStyle}
                                    >
                                      <UploadIcon sx={{ fontSize: 14 }} />
                                      Upload
                                    </button>
                                  ) : null}
                                </div>
                              );
                            }
                            if (o.status === "Cancelled") {
                              return (
                                <div
                                  key={o.orderNo}
                                  style={{ marginBottom: isMulti ? 4 : 0 }}
                                >
                                  <span style={{ fontSize: 11, color: C.textSub }}>
                                    —
                                  </span>
                                </div>
                              );
                            }
                            const isNonIpd = patient.patientType !== "IPD";
                            const stored = reports?.[o.orderNo];
                            const reported =
                              READY_STATUSES.includes(o.status) ||
                              stored?.status === "final";
                            // A study handed to the RIS is reportable like a completed one.
                            if (
                              !reported &&
                              o.status !== "Completed" &&
                              o.status !== SENT_TO_LAB
                            ) {
                              return (
                                <div key={o.orderNo} style={wrapStyle}>
                                  <span style={{ fontSize: 11, color: C.textSub }}>
                                    Complete test first
                                  </span>
                                  {isNonIpd ? (
                                    <button
                                      type="button"
                                      title="Upload report to health records"
                                      onClick={() => onUploadReport(patient)}
                                      style={btnStyle}
                                    >
                                      <UploadIcon sx={{ fontSize: 14 }} />
                                      Upload
                                    </button>
                                  ) : null}
                                </div>
                              );
                            }
                            return (
                              <div key={o.orderNo} style={wrapStyle}>
                                <button
                                  type="button"
                                  onClick={() => onGenerateReport(o, patient)}
                                  style={btnStyle}
                                >
                                  <SvgIcon name="reports" size={13} color={C.primary} />
                                  {reported
                                    ? "View"
                                    : stored
                                      ? "Continue draft"
                                      : "Generate"}
                                </button>
                                {isNonIpd ? (
                                  <button
                                    type="button"
                                    title="Upload report to health records"
                                    onClick={() => onUploadReport(patient)}
                                    style={btnStyle}
                                  >
                                    <UploadIcon sx={{ fontSize: 14 }} />
                                    Upload
                                  </button>
                                ) : null}
                              </div>
                            );
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const PatientRow = React.memo(PatientRowBase);

export default function IpdRadiologyOrders({ patientTypeFilter = "ALL" }) {
  const brandingData = useContext(BrandingContext);
  const { userInfo } = usePermissions();
  const [patients, setPatients] = useState([]);
  const patientsRef = useRef(patients);
  useEffect(() => {
    patientsRef.current = patients;
  }, [patients]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const expandedIdsRef = useRef(expandedIds);
  useEffect(() => {
    expandedIdsRef.current = expandedIds;
  }, [expandedIds]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All statuses");
  const [priorityFilter, setPriorityFilter] = useState("All priorities");
  const [modalityFilter, setModalityFilter] = useState("All modalities");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [kpiStats, setKpiStats] = useState(EMPTY_KPI_STATS);
  const [kpiStatsLoading, setKpiStatsLoading] = useState(true);
  const kpiStatsSeqRef = useRef(0);
  // Reports already loaded for the visible orders, keyed by orderNo — a cache
  // for the table's Report column, not the source of truth (that is
  // api/radiologyReportApi.js, which the modal reads and writes).
  const [reports, setReports] = useState({});
  const [reportModal, setReportModal] = useState(null);
  const [rejectionModal, setRejectionModal] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [uploadModal, setUploadModal] = useState(null);
  const dateFromRef = useRef(null);
  const dateToRef = useRef(null);

  const openDatePicker = (ref) => {
    if (ref.current?.showPicker) ref.current.showPicker();
    else ref.current?.focus();
  };

  const loadPatientOrdersRef = useRef(null);
  const fetchRadiologyOrdersRef = useRef(null);
  const listFetchSeqRef = useRef(0);
  const detailFetchInFlightRef = useRef(new Map());
  const [detailLoadingIds, setDetailLoadingIds] = useState(() => new Set());
  const [statusUpdatingOrderNos, setStatusUpdatingOrderNos] = useState(
    () => new Set(),
  );
  const [acknowledgedByRow, setAcknowledgedByRow] = useState({});
  const [acknowledgingIds, setAcknowledgingIds] = useState(() => new Set());
  const [cancelModal, setCancelModal] = useState(null);
  const [dispatchModal, setDispatchModal] = useState(null);
  const [dispatchMap, setDispatchMap] = useState(() => readDispatchMap());
  const statusPersistedOrderNosRef = useRef(new Set());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [
    patientTypeFilter,
    debouncedSearch,
    statusFilter,
    priorityFilter,
    modalityFilter,
    dateFrom,
    dateTo,
  ]);

  const listQuery = useMemo(
    () => ({
      patientType: patientTypeFilter,
      search: debouncedSearch,
      priority: priorityFilter,
      dateFrom,
      dateTo,
    }),
    [
      patientTypeFilter,
      debouncedSearch,
      priorityFilter,
      dateFrom,
      dateTo,
    ],
  );

  const kpiQuery = listQuery;

  const fetchKpiStats = useCallback(async () => {
    const requestId = ++kpiStatsSeqRef.current;
    setKpiStatsLoading(true);
    try {
      const allPatients = await fetchAllRadiologyTestPatients(
        axios,
        getAuthHeaders(true),
        kpiQuery,
        { skipGlobalLoader: true },
      );
      if (requestId !== kpiStatsSeqRef.current) return;
      const orders = allPatients.flatMap((p) => p.orders);
      setKpiStats(computeRadiologyDashboardStats(orders));
    } catch (err) {
      if (requestId !== kpiStatsSeqRef.current) return;
      console.error("Failed to load radiology dashboard KPI stats:", err);
    } finally {
      if (requestId === kpiStatsSeqRef.current) {
        setKpiStatsLoading(false);
      }
    }
  }, [kpiQuery]);

  const fetchKpiStatsRef = useRef(fetchKpiStats);
  fetchKpiStatsRef.current = fetchKpiStats;

  useEffect(() => {
    fetchKpiStatsRef.current?.();
  }, [fetchKpiStats]);

  const fetchRadiologyOrders = useCallback(async ({ silent = false } = {}) => {
    const requestId = ++listFetchSeqRef.current;
    if (!silent) setOrdersLoading(true);
    try {
      // Full walk then client-sort/page — same reason as lab: a brand-new
      // Emergency/OPD study must surface on page 1, not wherever the server
      // happened to page it.
      const next = await fetchAllRadiologyTestPatients(
        axios,
        getAuthHeaders(true),
        listQuery,
        { skipGlobalLoader: !!silent },
      );
      if (requestId !== listFetchSeqRef.current) return;
      setPatients((prev) =>
        replacePatientsPreservingStatus(
          prev,
          next,
          statusPersistedOrderNosRef.current,
        ),
      );
      setOrdersError(null);

      const openIds = [...expandedIdsRef.current];
      openIds.forEach((rowId) => {
        const baseId = String(rowId).split("::")[0];
        const target = next.find((p) => String(p.id) === baseId);
        if (target) loadPatientOrdersRef.current?.(target, { silent: true });
      });
    } catch (err) {
      if (requestId !== listFetchSeqRef.current) return;
      const status = err.response?.status;
      const serverMessage =
        err.response?.data?.message || err.response?.data?.error;
      console.error(
        "Failed to fetch radiology orders:",
        status,
        err.response?.data || err.message,
      );
      const detail = status
        ? `Could not load radiology orders (${status}${serverMessage ? `: ${serverMessage}` : ""})`
        : "Could not load radiology orders (network error)";
      setOrdersError(detail);
      if (!silent) toast.error(detail);
    } finally {
      if (requestId === listFetchSeqRef.current) {
        setOrdersLoading(false);
      }
    }
  }, [listQuery]);

  fetchRadiologyOrdersRef.current = fetchRadiologyOrders;

  useEffect(() => {
    fetchRadiologyOrdersRef.current?.({ silent: false });
  }, [listQuery]);

  const detailKeyFor = (patient) => {
    if (isIpdPatient(patient)) return `adm:${patient.admissionId}`;
    return `rt:${patient.radiologyTestId || patient.labTestId || patient.id}`;
  };

  const loadPatientOrders = useCallback(
    async (patient, { silent = false } = {}) => {
      const isIpd = isIpdPatient(patient);
      const detailKey = detailKeyFor(patient);
      if (
        !isIpd &&
        !patient.labTestId &&
        !patient.radiologyTestId &&
        !patient.appointmentId
      ) {
        return null;
      }
      if (isIpd && (patient.admissionId == null || patient.admissionId === "")) {
        return null;
      }

      const inflight = detailFetchInFlightRef.current.get(detailKey);
      if (inflight) return inflight;

      const run = (async () => {
        if (!silent) {
          setDetailLoadingIds((prev) => new Set(prev).add(detailKey));
        }
        try {
          const query = isIpd
            ? {
                admissionId: patient.admissionId,
                page: 1,
                pageSize: 50,
                patientType: "IPD",
              }
            : patient.radiologyTestId || patient.labTestId
              ? {
                  radiologyTestId: patient.radiologyTestId || patient.labTestId,
                  page: 1,
                  pageSize: 50,
                }
              : {
                  appointmentId: patient.appointmentId,
                  page: 1,
                  pageSize: 50,
                  patientType: "OPD",
                };

          const { patients: fetched } = await fetchRadiologyTestsPage(
            axios,
            getAuthHeaders(true),
            query,
            { skipGlobalLoader: true },
          );

          const match =
            fetched.find((p) => p.id === patient.id) ||
            fetched.find(
              (p) =>
                isIpd && String(p.admissionId) === String(patient.admissionId),
            ) ||
            fetched[0];

          if (!match) return null;

          const existing = patientsRef.current.find((p) =>
            samePatientRow(p, patient),
          );
          const orders = existing
            ? reconcileOrderStatuses(
                existing.orders,
                match.orders,
                statusPersistedOrderNosRef.current,
              )
            : match.orders;

          setPatients((prev) => {
            const target = prev.find((p) => samePatientRow(p, patient));
            if (
              target &&
              JSON.stringify(target.orders) === JSON.stringify(orders) &&
              target.name === match.name
            ) {
              return prev;
            }
            return prev.map((p) =>
              samePatientRow(p, patient)
                ? {
                    ...p,
                    ...match,
                    id: p.id,
                    orders,
                  }
                : p,
            );
          });

          return orders;
        } catch (err) {
          console.error(
            `Failed to fetch radiology studies for ${patient.name}:`,
            err.response?.data || err.message,
          );
          if (!silent) toast.error(`Could not load studies for ${patient.name}`);
          return null;
        } finally {
          if (!silent) {
            setDetailLoadingIds((prev) => {
              const next = new Set(prev);
              next.delete(detailKey);
              return next;
            });
          }
        }
      })();

      detailFetchInFlightRef.current.set(detailKey, run);
      try {
        return await run;
      } finally {
        detailFetchInFlightRef.current.delete(detailKey);
      }
    },
    [],
  );

  loadPatientOrdersRef.current = loadPatientOrders;

  const submitRadiologyTestStatusUpdates = useCallback(
    async (patient, orders, status, extraBody = {}) => {
      if (isIpdPatient(patient)) {
        const admissionId = String(patient.admissionId ?? "");
        const results = await Promise.all(
          orders.map((o) =>
            axios.post(
              `${process.env.REACT_APP_BACKEND_ROUTE_URL}update_lab_test_status/`,
              {
                admission_id: admissionId,
                order_no: o.orderNo,
                status,
                ...extraBody,
              },
              { headers: getAuthHeaders(true), skipGlobalLoader: true },
            ),
          ),
        );
        results.forEach(assertStatusUpdateOk);
        return;
      }
      const labTestPayload = {
        lab_tests: orders.map((o) => ({
          lab_test_id: resolveOpdLabTestId(patient, o),
          status,
          ...extraBody,
        })),
      };
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}change_patient_lab_test_status/`,
        JSON.stringify(labTestPayload),
        { headers: getAuthHeaders(true), skipGlobalLoader: true },
      );
      assertStatusUpdateOk(res);
    },
    [],
  );

  const patientRows = useMemo(
    () =>
      patients
        .flatMap((p) =>
          groupOrdersByBatchOnly(p.orders).map((b) => ({
            ...p,
            id: `${p.id}::${b.batchKey}`,
            orders: b.orders,
          })),
        )
        .sort((a, b) => latestOrderMs(b.orders) - latestOrderMs(a.orders)),
    [patients],
  );

  const visibleOrderNosKey = useMemo(
    () =>
      patients
        .flatMap((p) => (p.orders || []).map((o) => o.orderNo))
        .filter(Boolean)
        .sort()
        .join("|"),
    [patients],
  );

  useEffect(() => {
    if (!visibleOrderNosKey) return undefined;
    const nos = visibleOrderNosKey.split("|");
    let cancelled = false;
    fetchReportsMap(nos).then((map) => {
      if (cancelled) return;
      setReports((prev) => ({ ...map, ...prev }));
      const finalNos = Object.entries(map)
        .filter(([, r]) => r?.status === "final")
        .map(([no]) => no);
      if (!finalNos.length) return;
      finalNos.forEach((no) => statusPersistedOrderNosRef.current.add(no));
      const finalSet = new Set(finalNos);
      setPatients((prev) => {
        let changed = false;
        const next = prev.map((p) => ({
          ...p,
          orders: p.orders.map((o) => {
            if (!finalSet.has(o.orderNo) || o.status === "Cancelled" || o.status === "Reported" || o.status === "Verified") {
              return o;
            }
            changed = true;
            return { ...o, status: "Reported" };
          }),
        }));
        return changed ? next : prev;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [visibleOrderNosKey]);

  const filteredPatients = useMemo(() => {
    let rows = patientRows;
    if (statusFilter !== "All statuses") {
      rows = rows
        .map((p) => ({
          ...p,
          orders: p.orders.filter((o) => {
            // "Submitted for Review" is a Reported study already sent to a
            // diagnostic doctor; "Reported" then means not yet submitted.
            const submitted = o.status === "Reported" && isReviewSubmitted(o);
            if (statusFilter === "Submitted for Review") return submitted;
            if (statusFilter === "Reported") {
              return o.status === "Reported" && !submitted;
            }
            return o.status === statusFilter;
          }),
        }))
        .filter((p) => p.orders.length > 0);
    }
    if (modalityFilter !== "All modalities") {
      rows = rows
        .map((p) => ({
          ...p,
          orders: p.orders.filter((o) => o.modality === modalityFilter),
        }))
        .filter((p) => p.orders.length > 0);
    }
    return rows;
  }, [patientRows, statusFilter, modalityFilter]);

  const totalCount = filteredPatients.length;
  const totalStudies = filteredPatients.reduce(
    (sum, p) => sum + p.orders.length,
    0,
  );

  const totalPages =
    pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;

  const pagedPatients = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredPatients.slice(start, start + pageSize);
  }, [filteredPatients, page, pageSize]);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const toggleExpand = useCallback((row) => {
    const id = row.id;
    const opening = !expandedIdsRef.current.has(id);
    setExpandedIds((prev) => {
      const next = opening ? new Set([id]) : new Set(prev);
      if (!opening) next.delete(id);
      expandedIdsRef.current = next;
      return next;
    });
    if (opening) {
      // Bulk list already includes this row's orders — expand is display-only.
      // Detail refetch is reserved for Acknowledge when the row has nothing cached.
      if (!(row.orders || []).length) {
        loadPatientOrdersRef.current?.(row);
      }
    }
  }, []);

  const applyOrderStatusOnPatients = (patient, orderNo, patch) =>
    setPatients((prev) =>
      prev.map((p) =>
        samePatientRow(p, patient)
          ? {
              ...p,
              orders: p.orders.map((o) =>
                o.orderNo === orderNo ? { ...o, ...patch } : o,
              ),
            }
          : p,
      ),
    );

  // [RIS] One study per call, from the "Sent to lab" pick; resolves to whether
  // the RIS accepted it.
  const sendStudyToRis = useCallback(
    (order, patient) =>
      sendRadiologyOrderToRis({
        patientId: patient.patientId,
        serviceIds: [order.medicalServiceNo ?? order.medicalServiceId]
          .filter((id) => id != null && id !== "" && id !== "-")
          .map(String),
        careContextId:
          patient.careContextId || order.careContextReferenceId || "",
        doctorName: order.doctor && order.doctor !== "-" ? order.doctor : "",
        orderNo: order.orderNo,
        userInfo,
        headers: getAuthHeaders(),
      }),
    [userInfo],
  );

  const handleStatusChange = useCallback(
    async (order, nextStatus, row) => {
      if (order.status === nextStatus) return;
      if (nextStatus === "Cancelled") {
        // Cancel is only offered before acknowledgement.
        if (!ACK_PENDING_STATUSES.includes(order.status)) return;
        setCancelModal({ order, row });
        return;
      }
      // Acknowledging is its own path (charge capture). After that, Acknowledged
      // is not a status you can move back to from this dropdown.
      if (nextStatus === "Acknowledged") return;
      if (nextStatus === SEND_TO_OUTSOURCED) return;
      const previousStatus = order.status;

      setStatusUpdatingOrderNos((prev) => new Set(prev).add(order.orderNo));
      try {
        // A study only reads "Sent to lab" once the RIS has accepted it; if
        // the hand-off fails it stays Acknowledged so it can be sent again.
        if (nextStatus === SENT_TO_LAB && !(await sendStudyToRis(order, row))) {
          return;
        }
        applyOrderStatusOnPatients(row, order.orderNo, {
          status: nextStatus,
          modalityUpdatedAt: new Date().toISOString(),
        });
        await submitRadiologyTestStatusUpdates(row, [order], nextStatus);
        statusPersistedOrderNosRef.current.add(order.orderNo);
        toast.success(`${order.orderNo} → ${nextStatus}`);
      } catch (err) {
        console.error(
          `Failed to update status for ${order.orderNo}:`,
          err.response?.data || err.message,
        );
        applyOrderStatusOnPatients(row, order.orderNo, {
          status: previousStatus,
        });
        toast.error(`Could not update ${order.orderNo}. Please try again.`);
      } finally {
        setStatusUpdatingOrderNos((prev) => {
          const next = new Set(prev);
          next.delete(order.orderNo);
          return next;
        });
      }
    },
    [submitRadiologyTestStatusUpdates, sendStudyToRis],
  );

  const handleSendToOutsourced = useCallback((patient, orders) => {
    const scope = resolveBillingScope(patient);
    if (!scope) {
      toast.error(
        patient?.patientType === "EMERGENCY"
          ? "Missing emergency visit id — cannot send to outsourced vendor"
          : "Missing admission id — cannot send to outsourced vendor",
      );
      return;
    }
    const toMove = (orders || []).filter(
      (o) =>
        !dispatchMap[String(o.orderNo)] &&
        !ACK_PENDING_STATUSES.includes(o.status) &&
        o.status !== "Cancelled",
    );
    if (toMove.length === 0) {
      toast.info("No acknowledged studies available to outsource.");
      return;
    }
    setDispatchModal({
      patient,
      group: { orders: toMove },
      toMove,
    });
  }, [dispatchMap]);

  const handleConfirmOutsourceDispatch = useCallback(
    async ({ vendor }) => {
      const ctx = dispatchModal;
      if (!ctx || !vendor) return;
      setDispatchModal(null);

      const billingScope = resolveBillingScope(ctx.patient);
      if (!billingScope) {
        toast.error("Could not resolve billing scope for vendor charge.");
        return;
      }

      const orderNos = ctx.toMove.map((o) => o.orderNo);
      let chargeEventsByOrder = {};
      const isOpd = billingScope.type === "opd";

      // IPD may already have an in-house charge from Acknowledge — reverse it
      // before posting the vendor rate so the bill is not doubled.
      if (billingScope.type === "ipd") {
        const priorIds = ctx.toMove
          .map((o) => o.chargeEventId)
          .filter(Boolean);
        if (priorIds.length) {
          try {
            await reverseVendorCharges(
              billingScope,
              priorIds,
              "Replaced by outsourced vendor charge",
            );
          } catch (err) {
            console.warn(
              "Could not reverse prior radiology charges before outsourcing:",
              err?.message || err,
            );
          }
        }
      }

      // OPD has no vendor charge-capture endpoint — skip billing, mirroring
      // the lab board's handling of OPD outsourced dispatch.
      if (!isOpd) {
        try {
          const { chargeEventsByOrder: captured, captured: count, bulk } =
            await captureVendorChargesForOrders(
              billingScope,
              ctx.toMove,
              vendor.vendor_id,
            );
          chargeEventsByOrder = captured;
          if (count === 0) {
            toast.error(
              "Could not capture vendor charges — no billable studies on this selection",
            );
            return;
          }
          if (bulk?.summary?.failed > 0) {
            toast.warn(
              `Vendor charges captured for ${count} stud${count === 1 ? "y" : "ies"}, but ${bulk.summary.failed} failed`,
            );
          }
        } catch (err) {
          console.error(
            "Radiology vendor charge capture failed:",
            err.response?.data || err.message,
          );
          toast.error(
            err.response?.data?.message ||
              err.message ||
              "Could not capture vendor charge.",
          );
          return;
        }
      }

      saveDispatch({
        orderNos,
        vendor,
        chargeEventsByOrder,
      });
      setDispatchMap(readDispatchMap());

      setPatients((prev) =>
        prev.map((p) =>
          samePatientRow(p, ctx.patient)
            ? {
                ...p,
                orders: p.orders.map((o) =>
                  chargeEventsByOrder[o.orderNo]
                    ? {
                        ...o,
                        chargeEventId: chargeEventsByOrder[o.orderNo],
                      }
                    : o,
                ),
              }
            : p,
        ),
      );

      toast.success(
        isOpd
          ? `Sent to ${vendor.vendor_name}`
          : `Sent to ${vendor.vendor_name} — vendor charge captured`,
      );
    },
    [dispatchModal],
  );

  const handleConfirmCancelOrder = useCallback(
    async (reason) => {
      const pending = cancelModal;
      if (!pending) return;
      const { order, row } = pending;
      const isIpd = isIpdPatient(row);
      const admissionId = String(row?.admissionId ?? "");

      if (isIpd && !admissionId) {
        throw new Error("Missing admission id — cannot cancel this study");
      }

      setStatusUpdatingOrderNos((prev) => new Set(prev).add(order.orderNo));
      try {
        if (isIpd) {
          let chargeEventId = order.chargeEventId || null;
          try {
            chargeEventId = await resolveChargeEventIdFromLedger(
              admissionId,
              order,
            );
          } catch (lookupErr) {
            console.warn(
              `Could not resolve charge_event_id for ${order.orderNo}:`,
              lookupErr?.response?.data || lookupErr?.message,
            );
          }

          if (chargeEventId) {
            const cancelRes = await cancelCharge(
              admissionId,
              chargeEventId,
              reason,
            );
            const action = String(
              cancelRes?.cancel_result?.action ||
                cancelRes?.action ||
                "CANCELLED",
            ).toUpperCase();
            if (action === "IGNORED") {
              toast.info(
                `${order.orderNo} charge was still PENDING — cancel ignored on ledger`,
              );
            }
          } else {
            toast.warn(
              `${order.orderNo}: no charge_event_id found — order will cancel without ledger reversal`,
            );
          }
        }

        await submitRadiologyTestStatusUpdates(row, [order], "Cancelled");
        statusPersistedOrderNosRef.current.add(order.orderNo);
        applyOrderStatusOnPatients(row, order.orderNo, {
          status: "Cancelled",
          chargeEventId: null,
        });
        setCancelModal(null);
        toast.success(`${order.orderNo} → Cancelled`);
      } catch (err) {
        console.error(
          `Failed to cancel ${order.orderNo}:`,
          err.response?.data || err.message,
        );
        throw new Error(
          err.response?.data?.message ||
            err.message ||
            `Could not cancel ${order.orderNo}`,
        );
      } finally {
        setStatusUpdatingOrderNos((prev) => {
          const next = new Set(prev);
          next.delete(order.orderNo);
          return next;
        });
      }
    },
    [cancelModal, submitRadiologyTestStatusUpdates],
  );

  const handleAcknowledgeBatch = useCallback(
    async (row, specificOrders) => {
      if (acknowledgingIds.has(row.id)) return;
      const isIpd = isIpdPatient(row);
      const admissionId = String(row?.admissionId ?? "");
      if (isIpd && !admissionId) {
        toast.error(`Could not acknowledge ${row.name} — missing admission id`);
        return;
      }
      if (
        !isIpd &&
        !row.labTestId &&
        !row.radiologyTestId &&
        row.orders.every((o) => !o.orderNo)
      ) {
        toast.error(`Could not acknowledge ${row.name} — missing order id`);
        return;
      }
      const pending =
        Array.isArray(specificOrders) && specificOrders.length > 0
          ? specificOrders.filter((o) =>
              ACK_PENDING_STATUSES.includes(o.status),
            )
          : ordersAwaitingAck(row.orders);
      if (pending.length === 0) return;

      setAcknowledgingIds((prev) => new Set(prev).add(row.id));
      let statusPosted = false;
      try {
        let ordersToAck = pending;
        if (isIpd && !expandedIdsRef.current.has(row.id)) {
          setExpandedIds(() => {
            const next = new Set([row.id]);
            expandedIdsRef.current = next;
            return next;
          });
          const detailOrders = await loadPatientOrders(row);
          if (Array.isArray(detailOrders) && detailOrders.length > 0) {
            const wanted = new Set(pending.map((o) => o.orderNo));
            const enriched = detailOrders.filter((o) => wanted.has(o.orderNo));
            ordersToAck = enriched.length > 0 ? enriched : pending;
          }
        }

        await submitRadiologyTestStatusUpdates(row, ordersToAck, "Acknowledged");
        statusPosted = true;

        const ackedOrderNos = new Set(ordersToAck.map((o) => o.orderNo));
        const applyAcked = (chargeEventByOrderNo = {}) => {
          ackedOrderNos.forEach((no) =>
            statusPersistedOrderNosRef.current.add(no),
          );
          setPatients((prev) =>
            prev.map((p) =>
              samePatientRow(p, row)
                ? {
                    ...p,
                    orders: p.orders.map((o) =>
                      ackedOrderNos.has(o.orderNo)
                        ? {
                            ...o,
                            status: "Acknowledged",
                            ...(chargeEventByOrderNo[o.orderNo]
                              ? {
                                  chargeEventId:
                                    chargeEventByOrderNo[o.orderNo],
                                }
                              : {}),
                          }
                        : o,
                    ),
                  }
                : p,
            ),
          );
          setAcknowledgedByRow((prev) => ({
            ...prev,
            [row.id]: new Date().toISOString(),
          }));
        };

        applyAcked();

        if (!isIpd) {
          toast.success(
            `Acknowledged ${ordersToAck.length} stud${ordersToAck.length === 1 ? "y" : "ies"} — ${row.name}`,
          );
          return;
        }

        const chargeDate = todayChargeDate();
        const billable = ordersToAck
          .map((o) => ({
            order: o,
            medicalServiceId: billingMedicalServiceId(o),
          }))
          .filter((x) => x.medicalServiceId != null);

        if (billable.length === 0) {
          applyAcked();
          toast.error(
            `Acknowledged ${row.name}, but could not capture charges — no billing code found for these studies`,
          );
          return;
        }
        if (billable.length < ordersToAck.length) {
          toast.warn(
            `${ordersToAck.length - billable.length} of ${ordersToAck.length} stud${
              ordersToAck.length === 1 ? "y" : "ies"
            } for ${row.name} have no billing code and won't be charge-captured`,
          );
        }

        const chargeEventByOrderNo = {};
        if (billable.length === 1) {
          const { order, medicalServiceId } = billable[0];
          const res = await postCharge(admissionId, {
            medical_service_id: medicalServiceId,
            quantity: 1,
            charge_date: chargeDate,
          });
          const eventId = extractChargeEventId(res);
          if (eventId) chargeEventByOrderNo[order.orderNo] = eventId;
        } else {
          const charges = billable.map(({ medicalServiceId }) => ({
            medical_service_id: medicalServiceId,
            quantity: 1,
            charge_date: chargeDate,
          }));
          const res = await bulkPostCharges(admissionId, charges);
          const results = extractBulkChargeResults(res);
          billable.forEach(({ order }, idx) => {
            const matched =
              matchChargeEventIdForOrder(order, results) ||
              results[idx]?.chargeEventId;
            if (matched) chargeEventByOrderNo[order.orderNo] = matched;
          });
        }

        applyAcked(chargeEventByOrderNo);
        toast.success(
          `Acknowledged and captured charges for ${ordersToAck.length} stud${ordersToAck.length === 1 ? "y" : "ies"} — ${row.name}`,
        );
      } catch (err) {
        console.error(
          `Failed to acknowledge for ${row.name}:`,
          err.response?.data || err.message,
        );
        if (statusPosted) {
          toast.error(
            `Acknowledged ${row.name}, but charge capture failed — see console`,
          );
        } else {
          toast.error(
            `Could not acknowledge ${row.name} — see console for details`,
          );
        }
      } finally {
        setAcknowledgingIds((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      }
    },
    [acknowledgingIds, submitRadiologyTestStatusUpdates, loadPatientOrders],
  );

  const handleScheduleChange = useCallback(
    async (order, localValue, row) => {
      if (!localValue) return;
      if (!ACK_PENDING_STATUSES.includes(order.status)) {
        toast.error("Scheduling is only allowed before acknowledgement.");
        return;
      }
      const scheduledIso =
        localValue.length === 16 ? `${localValue}:00` : localValue;
      if (scheduledIso === order.scheduled) return;

      const previousScheduled = order.scheduled ?? null;
      const previousStatus = order.status;
      const nextStatus = ACK_PENDING_STATUSES.includes(order.status)
        ? "Scheduled"
        : order.status;

      applyOrderStatusOnPatients(row, order.orderNo, {
        scheduled: scheduledIso,
        status: nextStatus,
      });

      setStatusUpdatingOrderNos((prev) => new Set(prev).add(order.orderNo));
      try {
        await submitRadiologyTestStatusUpdates(row, [order], nextStatus, {
          scheduled_time: scheduledIso,
        });
        statusPersistedOrderNosRef.current.add(order.orderNo);
        toast.success(
          `${order.orderNo} scheduled for ${formatDateTime(scheduledIso)}`,
        );
      } catch (err) {
        console.error(
          `Failed to schedule ${order.orderNo}:`,
          err.response?.data || err.message,
        );
        applyOrderStatusOnPatients(row, order.orderNo, {
          scheduled: previousScheduled,
          status: previousStatus,
        });
        toast.error(`Could not schedule ${order.orderNo}. Please try again.`);
      } finally {
        setStatusUpdatingOrderNos((prev) => {
          const next = new Set(prev);
          next.delete(order.orderNo);
          return next;
        });
      }
    },
    [submitRadiologyTestStatusUpdates],
  );

  const handleDownloadConsolidated = useCallback(
    async (patient) => {
      const ready = (patient?.orders || []).filter(
        (o) => reports[o.orderNo]?.status === "final",
      );
      if (!ready.length) {
        toast.info("No finalized report to download yet — generate the report first.");
        return;
      }
      try {
        for (const order of ready) {
          await downloadRadiologyReportPdf({
            patient,
            order,
            report: reports[order.orderNo],
            brandingData,
            userInfo,
            patientLabTestId: pickRadiologyPatientLabTestId(order),
          });
        }
      } catch (err) {
        // downloadRadiologyReportPdf already toasts
      }
    },
    [reports, brandingData, userInfo],
  );

  const handleGenerateReport = useCallback((order, patient, { regenerate = false } = {}) => {
    if (order?.status === "Cancelled") return;
    const stored = reports[order?.orderNo];
    const reported =
      READY_STATUSES.includes(order?.status) || stored?.status === "final";
    if (
      !regenerate &&
      !isDiagnosticReportRejected(order) &&
      !reported &&
      order?.status !== "Completed" &&
      order?.status !== SENT_TO_LAB
    ) {
      toast.info("Complete the test before generating the report.");
      return;
    }
    setReportModal({ patient, order, regenerate });
  }, [reports]);

  const handleOpenRejected = useCallback((order, patient) => {
    const knownReason = String(order?.rejectionReason || "").trim();
    setRejectionModal({
      patient,
      order,
      reason: knownReason,
      loading: !knownReason,
    });
    if (knownReason) return;
    const pltId = pickRadiologyPatientLabTestId(order);
    if (!pltId) {
      setRejectionModal((prev) =>
        prev ? { ...prev, loading: false } : prev,
      );
      return;
    }
    fetchDiagnosticReview(pltId, { roleType: "radiologist" })
      .then((review) => {
        const reason = review?.rejection?.reason || "";
        setRejectionModal((prev) => {
          if (!prev || prev.order?.orderNo !== order.orderNo) return prev;
          return { ...prev, reason, loading: false };
        });
        if (!review?.rejection?.rejected && !reason) return;
        applyOrderStatusOnPatients(patient, order.orderNo, {
          reportPublishStatus: "rejected",
          rejectionReason: reason || order.rejectionReason || "",
        });
      })
      .catch(() => {
        setRejectionModal((prev) => {
          if (!prev || prev.order?.orderNo !== order.orderNo) return prev;
          return { ...prev, loading: false };
        });
      });
  }, []);

  const patchOrderPatientLabTestId = useCallback((patient, orderNo, patientLabTestId) => {
    if (!patient || !orderNo || !patientLabTestId) return;
    applyOrderStatusOnPatients(patient, orderNo, { patientLabTestId });
    setReportModal((prev) => {
      if (!prev?.patient || !samePatientRow(prev.patient, patient)) return prev;
      if (prev.order?.orderNo !== orderNo) return prev;
      return {
        ...prev,
        order: { ...prev.order, patientLabTestId },
      };
    });
  }, []);

  const openAssignModal = useCallback(
    async (order, patient) => {
      if (!order?.orderNo || !patient) return;

      let patientLabTestId = pickRadiologyPatientLabTestId(order);
      if (!patientLabTestId) {
        const loadingToast = toast.info("Loading study details…", {
          autoClose: false,
        });
        try {
          patientLabTestId = await lookupRadiologyPatientLabTestId(
            axios,
            getAuthHeaders(true),
            { patient, order },
          );
        } finally {
          toast.dismiss(loadingToast);
        }
      }

      if (patientLabTestId) {
        patchOrderPatientLabTestId(patient, order.orderNo, patientLabTestId);
      }

      const classificationName = order.department || "";
      setAssignModal({
        patientLabTestId: patientLabTestId || null,
        classificationName,
        serviceGroupId: RADIOLOGY_SERVICE_GROUP_ID,
        orderLabel: `${order.orderNo} · ${order.service || "Radiology study"}${classificationName ? ` · ${classificationName}` : ""}`,
        departmentLabel: classificationName,
        roleType: "radiologist",
        patient,
        orderNo: order.orderNo,
      });
    },
    [patchOrderPatientLabTestId],
  );

  const handleSubmitForReview = useCallback(
    (order) => {
      openAssignModal(order, reportModal?.patient);
    },
    [openAssignModal, reportModal?.patient],
  );

  const handleUploadReport = useCallback((patient) => {
    setUploadModal({
      abhaId: patient?.abhaId || "",
      patientId: patient?.patientId || "",
    });
  }, []);

  const handleSaveReport = useCallback(
    async (orderNo, patient, reportData) => {
      const no = String(orderNo || "").trim();
      if (!no) return;
      setReports((prev) => ({ ...prev, [no]: reportData }));
      if (reportData?.status !== "final") return;

      const order =
        (patient?.orders || []).find((o) => o.orderNo === no) ||
        (reportModal?.order?.orderNo === no ? reportModal.order : null) ||
        { orderNo: no };
      const rejected =
        isDiagnosticReportRejected(order) ||
        (reportModal?.order?.orderNo === no &&
          isDiagnosticReportRejected(reportModal.order));
      if (rejected) markReviewRevised(no);
      const reviewPatch = rejected
        ? { reportPublishStatus: "pending", rejectionReason: "" }
        : {};
      applyOrderStatusOnPatients(patient, no, {
        status: "Reported",
        ...reviewPatch,
      });
      setReportModal((prev) => {
        if (!prev?.order || prev.order.orderNo !== no) return prev;
        return {
          ...prev,
          regenerate: false,
          order: { ...prev.order, status: "Reported", ...reviewPatch },
          patient: prev.patient
            ? {
                ...prev.patient,
                orders: (prev.patient.orders || []).map((o) =>
                  o.orderNo === no
                    ? { ...o, status: "Reported", ...reviewPatch }
                    : o,
                ),
              }
            : prev.patient,
        };
      });
      statusPersistedOrderNosRef.current.add(no);
      try {
        await submitRadiologyTestStatusUpdates(patient, [order], "Reported");
      } catch (err) {
        console.warn(
          `Could not persist Reported for ${no}:`,
          err.response?.data || err.message,
        );
      }
    },
    [submitRadiologyTestStatusUpdates, reportModal],
  );

  return (
    <div>
      <KpiStrip stats={kpiStats} loading={kpiStatsLoading} />

      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search patient, UHID, order#, doctor, investigation…"
          style={{ ...inputStyle, flex: "1 1 260px" }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ ...inputStyle, width: 170 }}
        >
          <option>All statuses</option>
          {STATUS_OPTIONS.flatMap((s) =>
            s === "Reported" ? [s, "Submitted for Review"] : [s],
          ).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          style={{ ...inputStyle, width: 150 }}
        >
          <option>All priorities</option>
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <select
          value={modalityFilter}
          onChange={(e) => setModalityFilter(e.target.value)}
          style={{ ...inputStyle, width: 160 }}
        >
          <option>All modalities</option>
          {MODALITY_OPTIONS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <style>{`
          .radiology-date-input::-webkit-calendar-picker-indicator { opacity: 0; position: absolute; right: 0; width: 28px; height: 100%; cursor: pointer; }
        `}</style>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ position: "relative" }}>
            <input
              ref={dateFromRef}
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              className="radiology-date-input"
              style={{ ...inputStyle, width: 150, paddingRight: 32 }}
              title="From date"
            />
            <span
              onClick={() => openDatePicker(dateFromRef)}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                cursor: "pointer",
              }}
            >
              <SvgIcon name="calendar" size={14} color={C.textSub} />
            </span>
          </div>
          <span style={{ fontSize: 12, color: C.textSub }}>to</span>
          <div style={{ position: "relative" }}>
            <input
              ref={dateToRef}
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              className="radiology-date-input"
              style={{ ...inputStyle, width: 150, paddingRight: 32 }}
              title="To date"
            />
            <span
              onClick={() => openDatePicker(dateToRef)}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                cursor: "pointer",
              }}
            >
              <SvgIcon name="calendar" size={14} color={C.textSub} />
            </span>
          </div>
        </div>
        <span
          style={{
            fontSize: 12,
            color: C.textSub,
            whiteSpace: "nowrap",
            marginLeft: "auto",
          }}
        >
          {filteredPatients.length} patient
          {filteredPatients.length === 1 ? "" : "s"} · {totalStudies} stud
          {totalStudies === 1 ? "y" : "ies"}
        </span>
        <button
          type="button"
          title="Refresh orders"
          onClick={() => {
            const empty = new Set();
            expandedIdsRef.current = empty;
            setExpandedIds(empty);
            fetchRadiologyOrdersRef.current?.({ silent: false });
            fetchKpiStatsRef.current?.();
          }}
          disabled={ordersLoading}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            borderRadius: 8,
            border: `1.5px solid ${C.border}`,
            background: "#fff",
            color: C.text,
            fontSize: 12,
            fontWeight: 600,
            cursor: ordersLoading ? "default" : "pointer",
            fontFamily: "inherit",
            opacity: ordersLoading ? 0.6 : 1,
          }}
        >
          <SvgIcon name="refresh" size={14} color={C.textSub} />
          {ordersLoading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div style={{ ...cardStyle, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr
              style={{
                background: "#f8f9fa",
                fontSize: 11,
                fontWeight: 700,
                color: C.textSub,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              <th style={{ padding: "10px 12px" }}></th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>Patient</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>UHID</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>Studies</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>Ready</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>Pending</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>Latest order</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>Status</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>Consolidated report</th>
            </tr>
          </thead>
          <tbody>
            {ordersLoading && patients.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 32, textAlign: "center", color: C.textSub, fontSize: 13 }}>
                  Loading radiology orders…
                </td>
              </tr>
            ) : ordersError && patients.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 32, textAlign: "center", color: C.danger, fontSize: 13 }}>
                  {ordersError}
                </td>
              </tr>
            ) : pagedPatients.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 32, textAlign: "center", color: C.textSub, fontSize: 13 }}>
                  No matching orders.
                </td>
              </tr>
            ) : (
              pagedPatients.map((p) => (
                <PatientRow
                  key={p.id}
                  patient={p}
                  expanded={expandedIds.has(p.id)}
                  detailLoading={
                    detailLoadingIds.has(detailKeyFor(p)) &&
                    !(p.orders || []).length
                  }
                  onToggle={toggleExpand}
                  onDownloadConsolidated={handleDownloadConsolidated}
                  onGenerateReport={handleGenerateReport}
                  onOpenRejected={handleOpenRejected}
                  onUploadReport={handleUploadReport}
                  onStatusChange={handleStatusChange}
                  onSchedule={handleScheduleChange}
                  onAcknowledge={handleAcknowledgeBatch}
                  onSendToOutsourced={handleSendToOutsourced}
                  acknowledgedAt={acknowledgedByRow[p.id]}
                  statusUpdatingOrderNos={statusUpdatingOrderNos}
                  reports={reports}
                  dispatchMap={dispatchMap}
                  risOn={Boolean(userInfo?.ris_integrated)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 12,
          padding: "8px 4px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: C.textSub }}>Rows per page</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value) || DEFAULT_PAGE_SIZE);
              setPage(1);
            }}
            style={{ ...inputStyle, width: 80 }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: C.textSub }}>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page <= 1 || ordersLoading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            style={{
              padding: "6px 12px",
              borderRadius: 7,
              border: `1.5px solid ${C.border}`,
              background: "#fff",
              color: C.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: page <= 1 || ordersLoading ? "default" : "pointer",
              opacity: page <= 1 || ordersLoading ? 0.5 : 1,
              fontFamily: "inherit",
            }}
          >
            Prev
          </button>
          <button
            type="button"
            disabled={ordersLoading || page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            style={{
              padding: "6px 12px",
              borderRadius: 7,
              border: `1.5px solid ${C.border}`,
              background: "#fff",
              color: C.text,
              fontSize: 12,
              fontWeight: 600,
              cursor:
                ordersLoading || page >= totalPages ? "default" : "pointer",
              opacity:
                ordersLoading || page >= totalPages ? 0.5 : 1,
              fontFamily: "inherit",
            }}
          >
            Next
          </button>
        </div>
      </div>

      {rejectionModal && (
        <RejectedReportDialog
          order={rejectionModal.order}
          reason={rejectionModal.reason}
          loading={rejectionModal.loading}
          onClose={() => setRejectionModal(null)}
          onRegenerate={() => {
            const { order, patient } = rejectionModal;
            setRejectionModal(null);
            handleGenerateReport(order, patient, { regenerate: true });
          }}
        />
      )}

      {uploadModal && (
        <UploadFileModal
          isOpen={!!uploadModal}
          handleClose={() => setUploadModal(null)}
          abhaId={uploadModal.abhaId}
          patientId={uploadModal.patientId}
        />
      )}

      {reportModal && (
        <RadiologyReportModal
          patient={reportModal.patient}
          order={reportModal.order}
          report={reports[reportModal.order?.orderNo]}
          regenerate={!!reportModal.regenerate}
          onSave={handleSaveReport}
          onSubmitForReview={handleSubmitForReview}
          canSubmitForReview
          onClose={() => setReportModal(null)}
        />
      )}

      {assignModal && (
        <AssignDiagnosticDoctorsModal
          open={!!assignModal}
          onClose={() => setAssignModal(null)}
          patientLabTestId={assignModal.patientLabTestId}
          classificationName={assignModal.classificationName}
          serviceGroupId={assignModal.serviceGroupId}
          orderLabel={assignModal.orderLabel}
          departmentLabel={assignModal.departmentLabel}
          roleType={assignModal.roleType}
          onAssigned={() => {
            const { patient: assignPatient, orderNo } = assignModal || {};
            if (assignPatient && orderNo) {
              markReviewSubmitted(orderNo);
              applyOrderStatusOnPatients(assignPatient, orderNo, {
                reportPublishStatus: "pending",
                rejectionReason: "",
              });
              setReportModal((prev) => {
                if (!prev?.order || prev.order.orderNo !== orderNo) return prev;
                return {
                  ...prev,
                  order: {
                    ...prev.order,
                    reportPublishStatus: "pending",
                    rejectionReason: "",
                  },
                };
              });
            }
            setAssignModal(null);
          }}
        />
      )}

      {cancelModal && (
        <CancelOrderModal
          order={cancelModal.order}
          patientName={cancelModal.row?.name}
          onClose={() => setCancelModal(null)}
          onConfirm={handleConfirmCancelOrder}
        />
      )}

      {dispatchModal && (
        <DispatchToVendorModal
          group={dispatchModal.group}
          patient={dispatchModal.patient}
          requireVendor
          title="Send to outsourced vendor"
          confirmInHouseLabel="Select a vendor"
          onClose={() => setDispatchModal(null)}
          onConfirm={handleConfirmOutsourceDispatch}
        />
      )}
    </div>
  );
}
